import React from 'react';
import { motion } from 'framer-motion';

/**
 * SupremiumBadge - Diamond badge for Supremium Verified users
 * Displays an animated diamond icon with optional tooltip
 */
const SupremiumBadge = ({ size = 'md', showTooltip = true, className = '' }) => {
  const sizes = {
    xs: { badge: 'w-3 h-3', icon: 'w-2 h-2' },
    sm: { badge: 'w-4 h-4', icon: 'w-2.5 h-2.5' },
    md: { badge: 'w-5 h-5', icon: 'w-3 h-3' },
    lg: { badge: 'w-6 h-6', icon: 'w-4 h-4' },
    xl: { badge: 'w-8 h-8', icon: 'w-5 h-5' }
  };

  const sizeConfig = sizes[size] || sizes.md;

  return (
    <motion.div
      className={`relative inline-flex items-center justify-center ${className}`}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 400, damping: 15 }}
      title={showTooltip ? 'Supremium Verified' : ''}
    >
      {/* Glow effect */}
      <div className="absolute inset-0 animate-pulse">
        <div className={`${sizeConfig.badge} rounded-full bg-gradient-to-r from-yellow-300 to-amber-400 blur-sm opacity-60`} />
      </div>
      
      {/* Diamond badge */}
      <div className={`${sizeConfig.badge} relative flex items-center justify-center`}>
        {/* Diamond SVG */}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className={`${sizeConfig.icon} drop-shadow-lg`}
          style={{
            filter: 'drop-shadow(0 0 4px rgba(251, 191, 36, 0.6))'
          }}
        >
          <defs>
            <linearGradient id="diamondGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="25%" stopColor="#f59e0b" />
              <stop offset="50%" stopColor="#fcd34d" />
              <stop offset="75%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
            <linearGradient id="diamondShine" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.8" />
              <stop offset="50%" stopColor="#ffffff" stopOpacity="0" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0.4" />
            </linearGradient>
          </defs>
          {/* Diamond shape */}
          <path
            d="M12 2L2 9L12 22L22 9L12 2Z"
            fill="url(#diamondGradient)"
            stroke="#d97706"
            strokeWidth="0.5"
          />
          {/* Top facets */}
          <path
            d="M12 2L2 9H22L12 2Z"
            fill="url(#diamondShine)"
            opacity="0.6"
          />
          {/* Left facet */}
          <path
            d="M2 9L12 22L8 9H2Z"
            fill="#f59e0b"
            opacity="0.8"
          />
          {/* Right facet */}
          <path
            d="M22 9L12 22L16 9H22Z"
            fill="#d97706"
            opacity="0.8"
          />
          {/* Center shine */}
          <path
            d="M8 9H16L12 22L8 9Z"
            fill="url(#diamondGradient)"
          />
          {/* Sparkle accent */}
          <circle cx="10" cy="6" r="1" fill="#fff" opacity="0.9" />
        </svg>
      </div>
    </motion.div>
  );
};

/**
 * EnterpriseCrownBadge - Crown badge for Enterprise Supremium users
 */
const EnterpriseCrownBadge = ({ size = 'md', showTooltip = true, className = '' }) => {
  const sizes = {
    xs: { badge: 'w-3 h-3', icon: 'w-2 h-2' },
    sm: { badge: 'w-4 h-4', icon: 'w-2.5 h-2.5' },
    md: { badge: 'w-5 h-5', icon: 'w-3 h-3' },
    lg: { badge: 'w-6 h-6', icon: 'w-4 h-4' },
    xl: { badge: 'w-8 h-8', icon: 'w-5 h-5' }
  };

  const sizeConfig = sizes[size] || sizes.md;

  return (
    <motion.div
      className={`relative inline-flex items-center justify-center ${className}`}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 400, damping: 15 }}
      title={showTooltip ? 'Enterprise Supremium' : ''}
    >
      {/* Glow effect */}
      <div className="absolute inset-0 animate-pulse">
        <div className={`${sizeConfig.badge} rounded-full bg-gradient-to-r from-purple-400 to-pink-400 blur-sm opacity-60`} />
      </div>
      
      {/* Crown badge */}
      <div className={`${sizeConfig.badge} relative flex items-center justify-center`}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className={`${sizeConfig.icon} drop-shadow-lg`}
          style={{
            filter: 'drop-shadow(0 0 4px rgba(168, 85, 247, 0.6))'
          }}
        >
          <defs>
            <linearGradient id="crownGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a855f7" />
              <stop offset="50%" stopColor="#ec4899" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
          </defs>
          {/* Crown shape */}
          <path
            d="M2 17L4 8L8 12L12 4L16 12L20 8L22 17H2Z"
            fill="url(#crownGradient)"
            stroke="#7c3aed"
            strokeWidth="0.5"
          />
          {/* Crown base */}
          <rect x="2" y="17" width="20" height="3" rx="1" fill="url(#crownGradient)" />
          {/* Crown jewels */}
          <circle cx="12" cy="10" r="1.5" fill="#fbbf24" />
          <circle cx="7" cy="12" r="1" fill="#fbbf24" />
          <circle cx="17" cy="12" r="1" fill="#fbbf24" />
        </svg>
      </div>
    </motion.div>
  );
};

/**
 * VerifiedBadge - Generic verified badge that shows different types
 * @param {string} type - 'supremium' | 'enterprise_supremium' | 'verified' | 'admin' | 'business'
 */
export const VerifiedBadge = ({ type = 'verified', size = 'md', className = '' }) => {
  if (type === 'enterprise_supremium') {
    return <EnterpriseCrownBadge size={size} className={className} />;
  }
  if (type === 'supremium') {
    return <SupremiumBadge size={size} className={className} />;
  }

  const sizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8'
  };

  const sizeClass = sizes[size] || sizes.md;

  const badgeColors = {
    verified: { bg: 'bg-blue-500', icon: 'text-white' },
    admin: { bg: 'bg-red-500', icon: 'text-white' },
    business: { bg: 'bg-green-500', icon: 'text-white' }
  };

  const colors = badgeColors[type] || badgeColors.verified;

  return (
    <div
      className={`${sizeClass} ${colors.bg} rounded-full flex items-center justify-center ${className}`}
      title={type === 'admin' ? 'Admin' : type === 'business' ? 'Verified Business' : 'Verified'}
    >
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        className={`w-3/4 h-3/4 ${colors.icon}`}
      >
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
      </svg>
    </div>
  );
};

/**
 * UserBadge - Automatically shows the appropriate badge based on user data
 */
export const UserBadge = ({ user, size = 'md', className = '' }) => {
  if (!user) return null;

  // Enterprise Supremium - Crown badge
  if (user.subscription_tier === 'enterprise_supremium' || user.is_enterprise_supremium) {
    return <EnterpriseCrownBadge size={size} className={className} />;
  }

  if (user.subscription_tier === 'supremium' || user.is_supremium) {
    return <SupremiumBadge size={size} className={className} />;
  }

  if (user.is_admin) {
    return <VerifiedBadge type="admin" size={size} className={className} />;
  }

  if (user.is_verified) {
    if (user.is_business || user.account_type === 'business') {
      return <VerifiedBadge type="business" size={size} className={className} />;
    }
    return <VerifiedBadge type="verified" size={size} className={className} />;
  }

  return null;
};

export default SupremiumBadge;
